import { ChangeLog, Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  EditNotificationsValues,
  NotificationItem,
  NotificationItemComment,
} from '../types';

export const notificationsComponentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNotificationsList: build.query<Response<NotificationItem>, string>({
      query: (query) => ({
        url: `/v1/notifications${query}${
          query ? '&' : '?'
        }take=20&sortProp=createdAt&sortDir=desc`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationItem: build.query<{ data: NotificationItem }, string>({
      query: (id) => ({
        url: `/v1/notifications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationItemComment: build.query<
      Response<NotificationItemComment>,
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/${id}/comments?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addNotificationItemComment: build.mutation<
      { data: NotificationItemComment },
      { id: string; body: { content: string } }
    >({
      query: (data) => ({
        url: `/v1/notifications/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateNotificationIssue: build.mutation<
      { data: NotificationItem },
      {
        id: string;
        body: {
          responsibleId?: string;
          executorId?: string;
          status?: string;
          deadline?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationApplicationEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationChannelEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationOrganizationEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationTransactionEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationCashCollectionEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationEntity: build.query<{ data: NotificationItem }, string>({
      query: (id) => ({
        url: `/v1/notifications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationsTransactionTypes: build.query<
      Response<{ name: string; id: string }>,
      void
    >({
      query: () => ({
        url: '/v1/transactions-types?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchUpdateNotificationIssue: build.mutation<
      { data: number },
      EditNotificationsValues
    >({
      query: (data) => ({
        url: '/v1/notifications/batch-update',
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationEmployees: build.query<
      Response<{
        id: string;
        profile: {
          firstName: string;
          lastName: string;
        };
      }>,
      void
    >({
      query: () => ({
        url: `/v1/employees?take=9999`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchAddNotificationItemComment: build.mutation<
      { data: number },
      { ids: string[]; content: string }
    >({
      query: (data) => ({
        url: '/v1/notifications/batch-comments',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateNotificationIssueComment: build.mutation<
      { data: NotificationItemComment },
      {
        id: string;
        commentId: string;
        body: {
          content: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetNotificationsListQuery,
  useLazyGetNotificationItemCommentQuery,
  useAddNotificationItemCommentMutation,
  useUpdateNotificationIssueMutation,
  useLazyGetNotificationChangeLogsQuery,
  useLazyGetNotificationApplicationEntityQuery,
  useLazyGetNotificationChannelEntityQuery,
  useLazyGetNotificationOrganizationEntityQuery,
  useLazyGetNotificationTransactionEntityQuery,
  useLazyGetNotificationsTransactionTypesQuery,
  useBatchUpdateNotificationIssueMutation,
  useLazyGetNotificationEmployeesQuery,
  useBatchAddNotificationItemCommentMutation,
  useUpdateNotificationIssueCommentMutation,
  useLazyGetNotificationItemQuery,
  useLazyGetNotificationCashCollectionEntityQuery,
  useLazyGetNotificationEntityQuery,
} = notificationsComponentApi;

export const {
  endpoints: {
    getNotificationsList,
    addNotificationItemComment,
    getNotificationItemComment,
    updateNotificationIssue,
    getNotificationChangeLogs,
    getNotificationApplicationEntity,
    getNotificationChannelEntity,
    getNotificationOrganizationEntity,
    getNotificationTransactionEntity,
    getNotificationsTransactionTypes,
    batchUpdateNotificationIssue,
    getNotificationEmployees,
    batchAddNotificationItemComment,
    updateNotificationIssueComment,
    getNotificationItem,
    getNotificationCashCollectionEntity,
    getNotificationEntity,
  },
} = notificationsComponentApi;
